import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Label, SpecraBadge, SpecraButton, SpecraCheckbox, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { STATUS_TONE } from './constants';
const AREA_FIELDS = {
    Staffing: [
        {
            key: 'keyHiresConfirmed',
            label: 'Key hires confirmed',
            kind: 'checkbox'
        },
        {
            key: 'staffingPlanSubmitted',
            label: 'Staffing plan submitted',
            kind: 'checkbox'
        },
        {
            key: 'incumbentStatus',
            label: 'Incumbent status',
            kind: 'text',
            placeholder: 'e.g. None, Partial, Full'
        },
        {
            key: 'openVacancies',
            label: 'Open vacancies',
            kind: 'text',
            placeholder: 'Number of unfilled positions'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ],
    Financial: [
        {
            key: 'fundingAuthorized',
            label: 'Funding authorized',
            kind: 'checkbox'
        },
        {
            key: 'pricingReviewed',
            label: 'Pricing reviewed',
            kind: 'checkbox'
        },
        {
            key: 'budgetAllocated',
            label: 'Budget allocated',
            kind: 'checkbox'
        },
        {
            key: 'costRiskFlag',
            label: 'Cost risk flagged',
            kind: 'checkbox'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ],
    Subcontractor: [
        {
            key: 'teamingAgreementsExecuted',
            label: 'Teaming agreements executed',
            kind: 'checkbox'
        },
        {
            key: 'workshareLocked',
            label: 'Workshare percentages locked',
            kind: 'checkbox'
        },
        {
            key: 'subVettingComplete',
            label: 'Sub vetting complete',
            kind: 'checkbox'
        },
        {
            key: 'subcontractors',
            label: 'Subcontractor names',
            kind: 'text',
            placeholder: 'Comma-separated'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ],
    Schedule: [
        {
            key: 'contractStartDate',
            label: 'Contract start date',
            kind: 'text',
            placeholder: 'YYYY-MM-DD'
        },
        {
            key: 'popConfirmed',
            label: 'Period of performance confirmed',
            kind: 'checkbox'
        },
        {
            key: 'transitionPlanReady',
            label: 'Transition plan ready',
            kind: 'checkbox'
        },
        {
            key: 'keyMilestones',
            label: 'Key milestones',
            kind: 'textarea',
            placeholder: 'One per line'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ],
    Governance: [
        {
            key: 'pmoEstablished',
            label: 'PMO established',
            kind: 'checkbox'
        },
        {
            key: 'orgChartApproved',
            label: 'Org chart approved',
            kind: 'checkbox'
        },
        {
            key: 'reportingCadenceSet',
            label: 'Reporting cadence set',
            kind: 'checkbox'
        },
        {
            key: 'reportingCadence',
            label: 'Cadence description',
            kind: 'text',
            placeholder: 'e.g. Weekly status, monthly CDRL'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ],
    Customer: [
        {
            key: 'corIdentified',
            label: 'COR identified',
            kind: 'checkbox'
        },
        {
            key: 'corName',
            label: 'COR name',
            kind: 'text'
        },
        {
            key: 'kickoffScheduled',
            label: 'Kickoff meeting scheduled',
            kind: 'checkbox'
        },
        {
            key: 'kickoffDate',
            label: 'Kickoff date',
            kind: 'text',
            placeholder: 'YYYY-MM-DD'
        },
        {
            key: 'commPlanReady',
            label: 'Communication plan ready',
            kind: 'checkbox'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ],
    Artifacts: [
        {
            key: 'proposalStored',
            label: 'Proposal stored in tesoro',
            kind: 'checkbox'
        },
        {
            key: 'sowVersionConfirmed',
            label: 'SOW version confirmed',
            kind: 'checkbox'
        },
        {
            key: 'qualityPlanReady',
            label: 'Quality management plan ready',
            kind: 'checkbox'
        },
        {
            key: 'artifactIds',
            label: 'Artifact IDs',
            kind: 'text',
            placeholder: 'Comma-separated tesoro IDs'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ],
    Risk: [
        {
            key: 'riskRegisterSeeded',
            label: 'Risk register seeded',
            kind: 'checkbox'
        },
        {
            key: 'mitigationPlanApproved',
            label: 'Mitigation plan approved',
            kind: 'checkbox'
        },
        {
            key: 'openBlockersCount',
            label: 'Open blockers',
            kind: 'text',
            placeholder: '0'
        },
        {
            key: 'topRisk',
            label: 'Top risk',
            kind: 'text',
            placeholder: 'One-line description'
        },
        {
            key: 'notes',
            label: 'Notes',
            kind: 'textarea'
        }
    ]
};
function parseDataJson(dataJson) {
    if (!dataJson?.trim()) return {};
    try {
        return JSON.parse(dataJson);
    } catch  {
        return {};
    }
}
// Canonical form for dirty / echo comparisons: sorted keys, no whitespace,
// false booleans dropped. A checkbox toggled on then back off leaves
// {key: false} in form-state while the server row omits the key entirely —
// stripping false here makes both sides compare equal, preventing a spurious
// network write and keeping rows clean.
function canonicalize(data) {
    return JSON.stringify(Object.fromEntries(Object.keys(data).sort().filter((k)=>data[k] !== false).map((k)=>[
            k,
            data[k]
        ])));
}
// Strip false booleans before submitting — server rows never store explicit
// false, so omitting them keeps the stored JSON semantically clean.
function toSubmitJson(data) {
    return JSON.stringify(Object.fromEntries(Object.entries(data).filter(([, v])=>v !== false)));
}
export function ReadinessDetailEditor({ area, status, dataJson, isPending, saveError, onSave }) {
    const fields = AREA_FIELDS[area] ?? [];
    const [form, setForm] = useState(()=>parseDataJson(dataJson));
    const [dirty, setDirty] = useState(false);
    // Tracks the canonical form of what we submitted so we can detect the
    // roundtrip echo even if the storage layer normalizes JSON (JSONB reorders
    // keys / drops whitespace). Canonical = sorted keys, no whitespace.
    // Invariant: pangaea stores data_json verbatim today; if that ever changes
    // (value coercion, trimming) the echo compare must move server-side or use
    // a version token instead.
    // Cleared on saveError so a later revalidation can't falsely echo-match.
    const pendingSaveRef = useRef(null);
    // Mirrors form synchronously so the echo branch can check whether the user
    // typed again during the pending window without adding form to the deps
    // (which would create a setForm → form change → re-run → setForm loop).
    const formRef = useRef(form);
    useEffect(()=>{
        if (saveError) {
            pendingSaveRef.current = null;
        }
    }, [
        saveError
    ]);
    useEffect(()=>{
        if (pendingSaveRef.current !== null) {
            if (canonicalize(parseDataJson(dataJson)) === pendingSaveRef.current && canonicalize(formRef.current) === pendingSaveRef.current) {
                // Roundtrip confirmed: refetch echoed what we saved AND the form hasn't
                // diverged since (user didn't type during the pending window).
                pendingSaveRef.current = null;
                const echoed = parseDataJson(dataJson);
                formRef.current = echoed;
                setForm(echoed);
                setDirty(false);
                return;
            }
            if (canonicalize(formRef.current) !== pendingSaveRef.current) {
                // Form diverged while pending — this ref is stale; drop it so the next
                // save can stamp a fresh canonical rather than matching against the old one.
                pendingSaveRef.current = null;
            }
        }
        if (!dirty) {
            // No pending edits — sync to whatever the server says (initial load or
            // background revalidation with no in-progress work).
            const synced = parseDataJson(dataJson);
            formRef.current = synced;
            setForm(synced);
        }
    }, [
        dataJson,
        dirty
    ]);
    function set(key, value) {
        setForm((prev)=>{
            const next = {
                ...prev,
                [key]: value
            };
            formRef.current = next;
            return next;
        });
        setDirty(true);
    }
    function handleSave() {
        const canonical = canonicalize(form);
        if (canonical === canonicalize(parseDataJson(dataJson))) {
            // Form nets back to the server's current value — skip the mutation.
            // The roundtrip-echo path can't catch this (dataJson won't change),
            // so clear dirty here directly.
            setDirty(false);
            return;
        }
        pendingSaveRef.current = canonical;
        onSave(toSubmitJson(form));
    // onSaveError is called by the parent's mutate onError callback, which
    // clears pendingSaveRef so a later revalidation can't falsely echo-match.
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.preAwardV2.detailEditorForm,
        "data-area": area,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-sm font-medium",
                        children: area
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "outline",
                        className: STATUS_TONE[status] ?? STATUS_TONE.NotStarted,
                        children: status
                    })
                ]
            }),
            fields.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No structured fields defined for this area."
            }) : /*#__PURE__*/ _jsxs("form", {
                className: "space-y-3",
                onSubmit: (e)=>{
                    e.preventDefault();
                    handleSave();
                },
                children: [
                    fields.map((field)=>/*#__PURE__*/ _jsx("div", {
                            className: "space-y-1",
                            children: field.kind === 'checkbox' ? /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                        id: `${area}-${field.key}`,
                                        checked: Boolean(form[field.key]),
                                        onCheckedChange: (checked)=>set(field.key, Boolean(checked))
                                    }),
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: `${area}-${field.key}`,
                                        className: "text-sm font-normal",
                                        children: field.label
                                    })
                                ]
                            }) : field.kind === 'textarea' ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: `${area}-${field.key}`,
                                        className: "text-xs text-muted-foreground",
                                        children: field.label
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        id: `${area}-${field.key}`,
                                        rows: 3,
                                        value: form[field.key] ?? '',
                                        placeholder: field.placeholder,
                                        onChange: (e)=>set(field.key, e.target.value)
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: `${area}-${field.key}`,
                                        className: "text-xs text-muted-foreground",
                                        children: field.label
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: `${area}-${field.key}`,
                                        value: form[field.key] ?? '',
                                        placeholder: field.placeholder,
                                        onChange: (e)=>set(field.key, e.target.value)
                                    })
                                ]
                            })
                        }, field.key)),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "submit",
                                size: "sm",
                                disabled: !dirty || isPending,
                                "data-testid": Testids.preAwardV2.detailEditorSave,
                                children: isPending ? 'Saving…' : 'Save'
                            }),
                            saveError && /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-destructive",
                                children: "Save failed — try again"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
